import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Alert, Button, Col, Dropdown, Modal, Row } from 'react-bootstrap';
import { isChangeManager } from './changePermissions';
import { formatChangePublicId } from './changePublicId';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useModal } from '../../context/ModalContext';
import { changesApi } from '../../services/changesApi';
import type {
  ChangeAgendaSendKind,
  ChangeCabDeck,
  ChangeCabSlide,
  ChangeCabSlideField,
  ChangeMeeting,
  ChangeMeetingItem,
} from '../../types/changes';
import { getApiErrorMessage } from '../../utils/apiError';
import './cabPresentationTheme.css';
import {
  CAB_FONT_SCALE_DEFAULT,
  CAB_FONT_SCALE_MAX,
  CAB_FONT_SCALE_MIN,
  CAB_FONT_SCALE_STEP,
  clampCabFontScale,
  loadCabDeckTheme,
  loadCabFontScale,
  saveCabFontScale,
  type CabDeckTheme,
} from './cabDeckTheme';
import CabSlideStage from './components/CabSlideStage';
import CalendarMeetingAgendaPanel from './components/CalendarMeetingAgendaPanel';
import ChangeCabAddChangesModal from './components/ChangeCabAddChangesModal';

function formatClock(total: number): string {
  const safe = Math.max(0, Math.floor(total));
  const hours = Math.floor(safe / 3600);
  const minutes = String(Math.floor((safe % 3600) / 60)).padStart(2, '0');
  const seconds = String(safe % 60).padStart(2, '0');
  if (hours > 0) return `${hours}:${minutes}:${seconds}`;
  return `${minutes}:${seconds}`;
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

function isLastMeetingItem(meeting: ChangeMeeting, itemId?: number | null): boolean {
  if (!itemId || !meeting.items.length) return false;
  const ordered = [...meeting.items].sort((left, right) => left.sort_order - right.sort_order || left.id - right.id);
  return ordered[ordered.length - 1]?.id === itemId;
}

function meetingAgendaKinds(kind?: string | null): ChangeAgendaSendKind[] {
  if (kind === 'camud_go_no_go') return ['camud'];
  if (kind === 'riscos_impactos') return ['committee'];
  return ['committee', 'camud'];
}

function meetingAgendaWindow(scheduledAt?: string | null): { start: string; end: string } {
  const parsed = scheduledAt ? new Date(scheduledAt) : new Date();
  const base = Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  const start = new Date(base);
  start.setHours(0, 0, 0, 0);
  const end = new Date(base);
  end.setHours(23, 59, 59, 999);
  return { start: start.toISOString(), end: end.toISOString() };
}

function formatWhen(value?: string | null): string {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function MeetingTimesRecap({ meeting }: { meeting: ChangeMeeting }) {
  const items = [...meeting.items].sort((left, right) => left.sort_order - right.sort_order || left.id - right.id);
  const total = items.reduce((sum, item) => sum + (item.elapsed_seconds || 0), 0);
  const closedAt = formatWhen(meeting.closed_at);
  return (
    <div className="border rounded p-3 bg-white">
      <h3 className="h5 mb-1">Resumo dos tempos</h3>
      <p className="text-muted mb-3">
        Reunião encerrada{closedAt ? ` em ${closedAt}` : ''}. Os cronômetros pararam e o tempo de
        cada mudança ficou registrado.
      </p>
      <div className="table-responsive">
        <table className="table table-sm align-middle mb-3">
          <thead>
            <tr>
              <th>Mudança</th>
              <th>Status</th>
              <th className="text-end">Tempo</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id}>
                <td>
                  <div className="d-flex flex-wrap align-items-baseline gap-2">
                    <span className="fw-semibold text-body">
                      {formatChangePublicId(item.change_id)} {item.title}
                    </span>
                    <Link
                      to={`/changes/${item.change_id}`}
                      className="cab-meeting-change-link small"
                    >
                      Abrir ficha
                    </Link>
                  </div>
                </td>
                <td>{item.status_label || item.status || '—'}</td>
                <td className="text-end font-monospace">{formatClock(item.elapsed_seconds)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th colSpan={2}>Tempo total da reunião</th>
              <th className="text-end font-monospace">{formatClock(total)}</th>
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="small text-muted mb-0">
        O tempo de cada item é o relógio enquanto o cronômetro ficou em “Iniciar” (não o tempo que
        você ficou olhando a tela). Se ficou rodando ao sair da reunião, o valor sobe até Pausar,
        trocar de item ou Encerrar. Este resumo também aparece no card do calendário; Encerrar não
        apaga as mudanças da pauta.
      </p>
    </div>
  );
}

function SortableChange({
  item,
  active,
  canManage,
  onSelect,
  onRemove,
}: {
  item: ChangeMeetingItem;
  active: boolean;
  canManage: boolean;
  onSelect: () => void;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: item.id });
  return (
    <div
      ref={setNodeRef}
      className={`border rounded p-2 mb-2 ${active ? 'border-primary bg-light' : 'bg-white'}`}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <div className="d-flex gap-2 align-items-start">
        {canManage ? (
          <button
            type="button"
            className="btn btn-link p-0 text-muted"
            aria-label={`Arrastar ${item.title}`}
            {...attributes}
            {...listeners}
          >
            <i className="bi bi-grip-vertical" />
          </button>
        ) : null}
        <div className="flex-grow-1 min-w-0">
          <button
            type="button"
            className="cab-pauta-item-btn text-start w-100"
            onClick={onSelect}
          >
            <div className="fw-semibold text-body text-truncate">
              {formatChangePublicId(item.change_id)} {item.title}
            </div>
            <div className="small text-muted">
              {item.status_label || item.status} ·{' '}
              <span className="font-monospace">{formatClock(item.elapsed_seconds)}</span>
            </div>
          </button>
          <Link
            to={`/changes/${item.change_id}`}
            className="cab-meeting-change-link small"
            onClick={(event) => event.stopPropagation()}
          >
            Abrir ficha
          </Link>
        </div>
        {canManage ? (
          <Button size="sm" variant="outline-danger" onClick={onRemove} aria-label={`Remover ${item.title}`}>
            ×
          </Button>
        ) : null}
      </div>
    </div>
  );
}

const ChangeCabPage: React.FC = () => {
  const { meetingId } = useParams<{ meetingId: string }>();
  const id = Number(meetingId);
  const { user } = useAuth();
  const { showConfirm } = useModal();
  const autoNextLock = useRef(false);
  const meetingRef = useRef<ChangeMeeting | null>(null);
  const canManageRef = useRef(false);
  const leaveTimerGeneration = useRef(0);
  const canManage = isChangeManager(user);
  const [meeting, setMeeting] = useState<ChangeMeeting | null>(null);
  const [slides, setSlides] = useState<ChangeCabSlide[]>([]);
  const [fieldCatalog, setFieldCatalog] = useState<ChangeCabSlideField[]>([]);
  const [changeDeck, setChangeDeck] = useState<ChangeCabDeck | null>(null);
  const [deckTheme, setDeckTheme] = useState<CabDeckTheme>(() => loadCabDeckTheme());
  const [fontScale, setFontScale] = useState(() => loadCabFontScale());
  const [slideIndex, setSlideIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(0);
  const [addingChanges, setAddingChanges] = useState(false);
  const [pautaCollapsed, setPautaCollapsed] = useState(false);
  const [focusedItemId, setFocusedItemId] = useState<number | null>(null);
  const [presenting, setPresenting] = useState(false);
  const [showPresentMenu, setShowPresentMenu] = useState(false);
  const [lastItemNotice, setLastItemNotice] = useState(false);
  const [sendingAgenda, setSendingAgenda] = useState(false);
  const [agendaInfo, setAgendaInfo] = useState<string | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [meetingResult, slidesResult] = await Promise.allSettled([
        changesApi.getMeeting(id),
        changesApi.listCabSlides(),
      ]);
      if (meetingResult.status === 'rejected') {
        setError(getApiErrorMessage(meetingResult.reason, 'Falha ao abrir a reunião CAB.'));
        return;
      }
      setMeeting(meetingResult.value);
      setTick(0);
      if (slidesResult.status === 'fulfilled') {
        setSlides((slidesResult.value.items || []).filter((item) => item.is_active));
        setFieldCatalog(slidesResult.value.fields || []);
        setError(null);
        return;
      }
      setError(getApiErrorMessage(slidesResult.reason, 'A reunião abriu, mas os slides não carregaram.'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!Number.isFinite(id) || id <= 0) return;
    void load();
  }, [id, load]);

  useEffect(() => {
    const syncTheme = () => setDeckTheme(loadCabDeckTheme());
    window.addEventListener('storage', syncTheme);
    window.addEventListener('cab-deck-theme-updated', syncTheme);
    return () => {
      window.removeEventListener('storage', syncTheme);
      window.removeEventListener('cab-deck-theme-updated', syncTheme);
    };
  }, []);

  const apply = useCallback((next: ChangeMeeting, opts?: { keepFocusId?: number }) => {
    setMeeting((prev) => {
      const previousId = focusedItemId ?? prev?.current_item_id;
      if (next.current_item_id && next.current_item_id !== previousId && opts?.keepFocusId == null) {
        setSlideIndex(0);
      }
      return next;
    });
    setTick(0);
    if (opts?.keepFocusId != null) {
      setFocusedItemId(opts.keepFocusId);
      return;
    }
    if (next.current_item_id) {
      setFocusedItemId(next.current_item_id);
    }
  }, [focusedItemId]);

  const current = useMemo(
    () =>
      meeting?.items.find((item) => item.id === (focusedItemId ?? meeting.current_item_id))
      || meeting?.items[0]
      || null,
    [focusedItemId, meeting],
  );

  useEffect(() => {
    if (!current?.change_id) {
      setChangeDeck(null);
      return;
    }
    let cancelled = false;
    void changesApi
      .getChangeCabDeck(current.change_id)
      .then((deck) => {
        if (!cancelled) {
          setChangeDeck(deck);
          if (deck.theme) {
            setDeckTheme((prev) => ({ ...prev, ...deck.theme }));
          }
          setSlideIndex(0);
        }
      })
      .catch(() => {
        if (!cancelled) setChangeDeck(null);
      });
    return () => {
      cancelled = true;
    };
  }, [current?.change_id]);

  const liveElapsed = (current?.elapsed_seconds || 0) + (current?.running ? tick : 0);
  const remaining = Math.max(0, (meeting?.countdown_seconds || 0) - liveElapsed);
  const liveTotalElapsed = useMemo(() => {
    if (!meeting) return 0;
    return meeting.items.reduce((sum, item) => {
      if (item.id === current?.id) return sum + liveElapsed;
      // Item ainda “running” no backend (ex.: antes do stop assíncrono): não soma tick do item focado
      return sum + (item.elapsed_seconds || 0);
    }, 0);
  }, [meeting, current, liveElapsed]);
  const isLastItem = Boolean(meeting && current && isLastMeetingItem(meeting, current.id));
  const activeSlides = changeDeck?.slides?.length
    ? changeDeck.slides
        .filter((s) => s.is_active !== false)
        .map((s, index) => ({
          id: s.id || index + 1,
          title: s.title,
          field_keys: [],
          sort_order: s.sort_order,
          is_active: s.is_active,
        }))
    : slides;
  const slide = activeSlides[slideIndex] || activeSlides[0] || null;

  const selectPautaItem = useCallback(
    async (itemId: number) => {
      setFocusedItemId(itemId);
      setSlideIndex(0);
      setTick(0);
      if (!canManage || !meeting || meeting.status === 'encerrada') return;
      const runningOthers = meeting.items.filter((item) => item.running && item.id !== itemId);
      if (!runningOthers.length) return;
      try {
        let nextMeeting = meeting;
        for (const item of runningOthers) {
          nextMeeting = await changesApi.stopMeetingItem(meeting.id, item.id);
        }
        apply(nextMeeting, { keepFocusId: itemId });
        setError(null);
      } catch (err: unknown) {
        setError(getApiErrorMessage(err, 'Não foi possível parar o cronômetro da mudança anterior.'));
      }
    },
    [apply, canManage, meeting],
  );

  useEffect(() => {
    meetingRef.current = meeting;
  }, [meeting]);

  useEffect(() => {
    canManageRef.current = canManage;
  }, [canManage]);

  // Pausa o cronômetro ao sair da reunião (pagehide ou navegação SPA).
  // Timeout evita falso pause no remount do React.StrictMode em desenvolvimento.
  useEffect(() => {
    const generation = ++leaveTimerGeneration.current;
    const stopRunningTimers = () => {
      if (!canManageRef.current) return;
      const currentMeeting = meetingRef.current;
      if (!currentMeeting || currentMeeting.status === 'encerrada') return;
      const running = currentMeeting.items.filter((item) => item.running);
      running.forEach((item) => {
        void Promise.resolve(changesApi.stopMeetingItem(currentMeeting.id, item.id)).catch(() => undefined);
      });
    };
    const onPageHide = () => stopRunningTimers();
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      window.setTimeout(() => {
        if (leaveTimerGeneration.current === generation) {
          stopRunningTimers();
        }
      }, 40);
    };
  }, []);

  useEffect(() => {
    setTick(0);
  }, [current?.id]);

  useEffect(() => {
    if (!current?.running) return undefined;
    const timer = window.setInterval(() => setTick((value) => value + 1), 1000);
    return () => window.clearInterval(timer);
  }, [current?.running, current?.id]);

  useEffect(() => {
    if (!canManage || !meeting || !current?.running || remaining > 0 || autoNextLock.current) return;
    autoNextLock.current = true;
    void changesApi
      .nextMeetingItem(meeting.id, current.id)
      .then((next) => {
        apply(next);
        setLastItemNotice(isLastMeetingItem(next, next.current_item_id));
      })
      .catch((err: unknown) => setError(getApiErrorMessage(err, 'Não avançou para a próxima mudança.')))
      .finally(() => {
        autoNextLock.current = false;
      });
  }, [apply, canManage, meeting, current?.running, remaining]);

  const handleDragEnd = async (event: DragEndEvent) => {
    if (!meeting) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = meeting.items.findIndex((item) => item.id === active.id);
    const newIndex = meeting.items.findIndex((item) => item.id === over.id);
    const nextItems = arrayMove(meeting.items, oldIndex, newIndex);
    setMeeting({ ...meeting, items: nextItems });
    try {
      apply(await changesApi.reorderMeetingItems(meeting.id, nextItems.map((item) => item.id)));
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Não foi possível reordenar a pauta.'));
      try {
        await load();
      } catch {
        /* load já grava o erro na tela */
      }
    }
  };

  const runAction = useCallback(async (action: () => Promise<ChangeMeeting>, fallback: string) => {
    try {
      apply(await action());
      setError(null);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, fallback));
    }
  }, [apply]);

  const goNextChange = useCallback(() => {
    if (!canManage || !meeting || !current || meeting.status === 'encerrada') return;
    const previousId = current.id;
    void (async () => {
      try {
        const next = await changesApi.nextMeetingItem(meeting.id, current.id);
        apply(next);
        setLastItemNotice(isLastMeetingItem(next, next.current_item_id));
        if (next.current_item_id !== previousId) {
          setError(null);
        }
      } catch (err: unknown) {
        setError(getApiErrorMessage(err, 'Não foi para a próxima mudança.'));
      }
    })();
  }, [apply, canManage, current, meeting]);

  const goPrevSlide = useCallback(() => {
    setSlideIndex((value) => Math.max(0, value - 1));
  }, []);

  const goNextSlide = useCallback(() => {
    setSlideIndex((value) => {
      const last = Math.max(activeSlides.length - 1, 0);
      return Math.min(last, value + 1);
    });
  }, [activeSlides.length]);

  const bumpFontScale = useCallback((delta: number) => {
    setFontScale((prev) => {
      const next = clampCabFontScale(prev + delta);
      saveCabFontScale(next);
      return next;
    });
  }, []);

  const resetFontScale = useCallback(() => {
    setFontScale(CAB_FONT_SCALE_DEFAULT);
    saveCabFontScale(CAB_FONT_SCALE_DEFAULT);
  }, []);

  useEffect(() => {
    if (activeSlides.length <= 0) {
      setSlideIndex(0);
      return;
    }
    setSlideIndex((value) => Math.min(value, activeSlides.length - 1));
  }, [activeSlides.length]);

  const goCloseMeeting = useCallback(async () => {
    if (!canManage || !meeting || meeting.status === 'encerrada') return;
    const ok = await showConfirm({
      title: 'Encerrar reunião',
      message: 'Encerrar a CAB agora? O cronômetro para e o tempo fica registrado.',
      confirmLabel: 'Encerrar',
    });
    if (!ok) return;
    try {
      apply(await changesApi.closeMeeting(meeting.id));
      setLastItemNotice(false);
      setError(null);
      setPresenting(false);
      setShowPresentMenu(false);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Não foi possível encerrar.'));
    }
  }, [apply, canManage, meeting, showConfirm]);

  const toggleTimer = useCallback(() => {
    if (!canManage || !meeting || !current || meeting.status === 'encerrada') return;
    void runAction(
      () =>
        current.running
          ? changesApi.stopMeetingItem(meeting.id, current.id)
          : changesApi.startMeetingItem(meeting.id, current.id),
      'Não atualizou o cronômetro.',
    );
  }, [canManage, current, meeting, runAction]);

  const enterPresentation = useCallback(() => {
    if (!current || meeting?.status === 'encerrada') return;
    setPresenting(true);
    setShowPresentMenu(false);
    const root = document.documentElement;
    if (root.requestFullscreen) {
      void root.requestFullscreen().catch(() => undefined);
    }
  }, [current, meeting?.status]);

  const exitPresentation = useCallback(() => {
    setPresenting(false);
    setShowPresentMenu(false);
    if (document.fullscreenElement && document.exitFullscreen) {
      void document.exitFullscreen().catch(() => undefined);
    }
  }, []);

  // Trava scroll da página atrás do modo apresentação (barra vertical = página, não o slide).
  useEffect(() => {
    if (!presenting) return undefined;
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.classList.add('cab-cco-presenting');
    const onFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setPresenting(false);
        setShowPresentMenu(false);
      }
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      body.classList.remove('cab-cco-presenting');
      document.removeEventListener('fullscreenchange', onFullscreenChange);
    };
  }, [presenting]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (isTypingTarget(event.target)) return;
      if (!current || meeting?.status === 'encerrada') return;

      if (event.key === 'F5') {
        event.preventDefault();
        enterPresentation();
        return;
      }
      if (event.key === 'Escape' && presenting) {
        event.preventDefault();
        exitPresentation();
        return;
      }
      // Setas navegam slides na CAB e também no modo apresentação
      if (event.key === 'ArrowRight' || event.key === 'PageDown') {
        event.preventDefault();
        goNextSlide();
        return;
      }
      if (event.key === 'ArrowLeft' || event.key === 'PageUp') {
        event.preventDefault();
        goPrevSlide();
        return;
      }
      if (presenting && (event.key === ' ' || event.code === 'Space')) {
        event.preventDefault();
        toggleTimer();
        return;
      }
      if (event.key === '+' || event.key === '=' || event.code === 'NumpadAdd') {
        event.preventDefault();
        bumpFontScale(CAB_FONT_SCALE_STEP);
        return;
      }
      if (event.key === '-' || event.key === '_' || event.code === 'NumpadSubtract') {
        event.preventDefault();
        bumpFontScale(-CAB_FONT_SCALE_STEP);
        return;
      }
      if (event.key === '0' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        resetFontScale();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    current,
    enterPresentation,
    exitPresentation,
    goNextSlide,
    goPrevSlide,
    meeting?.status,
    presenting,
    toggleTimer,
    bumpFontScale,
    resetFontScale,
  ]);

  if (!Number.isFinite(id) || id <= 0) {
    return <Alert variant="danger">Reunião inválida.</Alert>;
  }

  return (
    <>
    <div className="container-fluid py-3" aria-hidden={presenting || undefined}>
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
        <div>
          <Link to="/changes/calendar" className="btn btn-outline-secondary btn-sm mb-2">
            <i className="bi bi-arrow-left me-1" aria-hidden="true" />
            Voltar ao calendário
          </Link>
          <h2 className="mb-0">CAB · {meeting?.title || (loading ? 'Carregando...' : 'Reunião indisponível')}</h2>
          <p className="text-muted mb-0">
            {meeting?.kind_label} · {meeting?.status_label} · tempo total {formatClock(liveTotalElapsed)}
          </p>
        </div>
        <div className="d-flex gap-2 flex-wrap">
        {canManage && meeting && meeting.items.length ? (
          <Button variant="outline-primary" onClick={() => setSendingAgenda(true)}>
            Enviar pauta
          </Button>
        ) : null}
        {current && meeting?.status !== 'encerrada' ? (
          <Button
            type="button"
            variant="primary"
            onClick={() => enterPresentation()}
            aria-label="Apresentar"
          >
            Apresentar (F5)
          </Button>
        ) : null}
        {canManage && meeting && meeting.status !== 'encerrada' ? (
          <Button variant="outline-danger" onClick={() => void goCloseMeeting()}>
            Encerrar
          </Button>
        ) : null}
        </div>
      </div>
      {agendaInfo ? (
        <Alert variant="success" dismissible onClose={() => setAgendaInfo(null)}>
          {agendaInfo}
        </Alert>
      ) : null}
      {lastItemNotice && meeting && meeting.status !== 'encerrada' ? (
        <Alert variant="warning" dismissible onClose={() => setLastItemNotice(false)}>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <span>Esta é a última mudança da pauta.</span>
            {canManage ? (
              <Button size="sm" variant="danger" onClick={() => void goCloseMeeting()}>
                Encerrar reunião
              </Button>
            ) : null}
          </div>
        </Alert>
      ) : null}
      {error ? (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
            <span>{error}</span>
            <Button size="sm" variant="outline-danger" onClick={() => void load()}>
              Tentar de novo
            </Button>
          </div>
        </Alert>
      ) : null}
      {!meeting ? (
        <p className="text-muted">{loading ? 'Carregando reunião...' : 'Não foi possível abrir a reunião.'}</p>
      ) : (
        <Row className="g-3">
          {pautaCollapsed ? (
            <Col xs="auto">
              <Button
                variant="outline-secondary"
                onClick={() => setPautaCollapsed(false)}
                aria-label="Mostrar pauta"
              >
                Mostrar pauta
              </Button>
            </Col>
          ) : (
          <Col lg={4}>
            <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
              <h6 className="mb-0">Pauta da reunião</h6>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => setPautaCollapsed(true)}
                aria-label="Ocultar pauta"
              >
                Ocultar pauta
              </Button>
            </div>
            <p className="small text-muted">
              {meeting.status === 'encerrada'
                ? 'Reunião encerrada. Os tempos de cada mudança ficam no resumo ao lado.'
                : 'Clique para escolher a mudança. Cronômetro da anterior para ao trocar. “Próxima mudança” não inicia sozinha — use Iniciar.'}
            </p>
            {canManage && meeting.status !== 'encerrada' ? (
              <Button className="mb-3" onClick={() => setAddingChanges(true)}>
                Adicionar mudanças
              </Button>
            ) : null}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => void handleDragEnd(e)}>
              <SortableContext items={meeting.items.map((item) => item.id)} strategy={verticalListSortingStrategy}>
                {meeting.items.map((item) => (
                  <SortableChange
                    key={item.id}
                    item={{ ...item, elapsed_seconds: item.id === current?.id ? liveElapsed : item.elapsed_seconds }}
                    active={item.id === current?.id}
                    canManage={canManage && meeting.status !== 'encerrada'}
                    onSelect={() => {
                      void selectPautaItem(item.id);
                    }}
                    onRemove={() => {
                      void runAction(
                        () => changesApi.removeMeetingItem(meeting.id, item.id),
                        'Não removeu a mudança.',
                      );
                    }}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </Col>
          )}
          <Col lg={pautaCollapsed ? 12 : 8}>
            {meeting.status === 'encerrada' ? (
              <MeetingTimesRecap meeting={meeting} />
            ) : current ? (
              <div>
                <div className="cab-cco-shell mb-3">
                  <CabSlideStage
                    slide={slide}
                    current={current}
                    remaining={remaining}
                    countdown={meeting.countdown_seconds}
                    elapsed={liveElapsed}
                    totalElapsed={liveTotalElapsed}
                    fieldCatalog={fieldCatalog}
                    meetingDate={meeting.scheduled_at}
                    theme={deckTheme}
                    deck={changeDeck}
                    deckSlideIndex={slideIndex}
                    fontScale={fontScale}
                    slideLabel={
                      activeSlides.length > 0
                        ? `${Math.min(slideIndex + 1, activeSlides.length)} de ${activeSlides.length}`
                        : undefined
                    }
                  />
                </div>
                <div className="d-flex flex-wrap gap-2 align-items-center" style={{ position: 'relative', zIndex: 2 }}>
                  {activeSlides.length > 1 ? (
                    <>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline-secondary"
                        disabled={slideIndex <= 0}
                        onClick={() => goPrevSlide()}
                      >
                        Slide anterior
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline-secondary"
                        disabled={slideIndex >= activeSlides.length - 1}
                        onClick={() => goNextSlide()}
                      >
                        Próximo slide
                      </Button>
                    </>
                  ) : null}
                  <div className="btn-group" role="group" aria-label="Tamanho da fonte do slide">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline-secondary"
                      disabled={fontScale <= CAB_FONT_SCALE_MIN}
                      onClick={() => bumpFontScale(-CAB_FONT_SCALE_STEP)}
                      aria-label="Diminuir fonte"
                      title="Diminuir fonte (-)"
                    >
                      A−
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline-secondary"
                      onClick={resetFontScale}
                      aria-label="Restaurar tamanho da fonte"
                      title="Restaurar 100% (Ctrl+0)"
                    >
                      {Math.round(fontScale * 100)}%
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline-secondary"
                      disabled={fontScale >= CAB_FONT_SCALE_MAX}
                      onClick={() => bumpFontScale(CAB_FONT_SCALE_STEP)}
                      aria-label="Aumentar fonte"
                      title="Aumentar fonte (+)"
                    >
                      A+
                    </Button>
                  </div>
                  {canManage && meeting.status !== 'encerrada' ? (
                    <>
                      <Button type="button" size="sm" variant="primary" onClick={() => toggleTimer()}>
                        {current.running ? 'Parar' : 'Iniciar'}
                      </Button>
                      <Button type="button" size="sm" variant="warning" onClick={() => goNextChange()}>
                        Próxima mudança
                      </Button>
                      {isLastItem ? (
                        <Button type="button" size="sm" variant="danger" onClick={() => void goCloseMeeting()}>
                          Encerrar reunião
                        </Button>
                      ) : null}
                    </>
                  ) : null}
                  <Link className="btn btn-sm btn-outline-secondary" to={`/changes/${current.change_id}`}>
                    Abrir ficha
                  </Link>
                </div>
                <p className="small text-muted mb-0 mt-2">
                  F5 ou Apresentar · Esc sai · ← → mudam de slide · Espaço no modo apresentação
                  inicia/pausa · +/− ajustam a fonte só nesta reunião (não altera o editor) · Ctrl+0
                  volta a 100% para comparar com a edição. O cronômetro some sozinho após alguns
                  segundos (modo fantasma) e volta ao mover o mouse; arraste-o para reposicionar
                  (duplo clique ou “Canto” restaura); Auto/Fixo controla se ele permanece visível.
                  O slide não tem scroll: se faltar espaço, use outra página no cadastro da
                  apresentação.
                </p>
              </div>
            ) : (
              <Alert variant="light" className="border">
                Inclua mudanças na pauta para apresentar.
              </Alert>
            )}
          </Col>
        </Row>
      )}
      <ChangeCabAddChangesModal
        show={addingChanges}
        meetingId={id}
        alreadyIncludedIds={(meeting?.items || []).map((item) => item.change_id)}
        onHide={() => setAddingChanges(false)}
        onAdded={apply}
      />
      {meeting ? (
        <Modal show={sendingAgenda} onHide={() => setSendingAgenda(false)} size="lg" animation={false}>
          <Modal.Header closeButton>
            <Modal.Title>Enviar pauta</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="small text-muted">
              Escolha os grupos e use «Escrever e-mail» para puxar o layout de Cadastros → E-mail da
              pauta já preenchido com as N mudanças desta reunião. O envio entra na mesma fila
              assíncrona do calendário.
            </p>
            <CalendarMeetingAgendaPanel
              meetingId={meeting.id}
              meetingTitle={meeting.title}
              meetingKind={meeting.kind}
              scheduledAt={meeting.scheduled_at}
              itemCount={meeting.items.length}
              previewChangeId={meeting.items[0]?.change_id}
              onSent={(message) => {
                setAgendaInfo(message);
                setSendingAgenda(false);
              }}
            />
          </Modal.Body>
        </Modal>
      ) : null}
    </div>
      {presenting && current && meeting ? (
        <div className="cab-cco-present-root" role="dialog" aria-modal="true" aria-label="Modo apresentação">
          <div className="position-absolute" style={{ top: 16, right: 16, zIndex: 4 }}>
            <Dropdown show={showPresentMenu} onToggle={(next) => setShowPresentMenu(Boolean(next))} align="end">
              <Dropdown.Toggle variant="outline-light" size="sm" aria-label="Menu da apresentação">
                Menu
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {canManage && meeting.status !== 'encerrada' ? (
                  <>
                    <Dropdown.Item onClick={toggleTimer}>
                      {current.running ? 'Parar cronômetro (Espaço)' : 'Iniciar cronômetro (Espaço)'}
                    </Dropdown.Item>
                    <Dropdown.Item onClick={goNextChange}>
                      Próxima mudança
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => void goCloseMeeting()}>
                      Encerrar reunião
                    </Dropdown.Item>
                  </>
                ) : null}
                {activeSlides.length > 1 ? (
                  <>
                    <Dropdown.Item disabled={slideIndex <= 0} onClick={goPrevSlide}>
                      Slide anterior (←)
                    </Dropdown.Item>
                    <Dropdown.Item disabled={slideIndex >= activeSlides.length - 1} onClick={goNextSlide}>
                      Próximo slide (→)
                    </Dropdown.Item>
                  </>
                ) : null}
                <Dropdown.Divider />
                <Dropdown.Item
                  disabled={fontScale >= CAB_FONT_SCALE_MAX}
                  onClick={() => bumpFontScale(CAB_FONT_SCALE_STEP)}
                >
                  Aumentar fonte (+) · {Math.round(fontScale * 100)}%
                </Dropdown.Item>
                <Dropdown.Item
                  disabled={fontScale <= CAB_FONT_SCALE_MIN}
                  onClick={() => bumpFontScale(-CAB_FONT_SCALE_STEP)}
                >
                  Diminuir fonte (−)
                </Dropdown.Item>
                <Dropdown.Item onClick={resetFontScale}>Restaurar fonte 100% (Ctrl+0)</Dropdown.Item>
                <Dropdown.Item as={Link} to={`/changes/${current.change_id}`}>
                  Abrir ficha
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={exitPresentation}>Sair da apresentação (Esc)</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
          <CabSlideStage
            slide={slide}
            current={current}
            remaining={remaining}
            countdown={meeting.countdown_seconds}
            elapsed={liveElapsed}
            totalElapsed={liveTotalElapsed}
            fieldCatalog={fieldCatalog}
            meetingDate={meeting.scheduled_at}
            theme={deckTheme}
            deck={changeDeck}
            deckSlideIndex={slideIndex}
            fontScale={fontScale}
            slideLabel={
              activeSlides.length > 0
                ? `${Math.min(slideIndex + 1, activeSlides.length)} de ${activeSlides.length}`
                : undefined
            }
          />
        </div>
      ) : null}
    </>
  );
};

export default ChangeCabPage;
